import { supabase } from '@/integrations/supabase/client';
import { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';

type LostItem = Tables<'lost_items'>;
type LostItemInsert = TablesInsert<'lost_items'>;
type LostItemUpdate = TablesUpdate<'lost_items'>;

// This file will contain centralized Supabase RPC calls for lost and found items.

export const getLostItems = async (from = 0, to = 19): Promise<LostItem[]> => {
    const { data, error } = await supabase.from('lost_items')
        .select('id, description, date, status, photo')
        .order('date', { ascending: false })
        .range(from, to);
    if (error) throw error;
    return data;
};

export const createLostItem = async (item: Omit<LostItemInsert, 'id' | 'sequential_number' | 'history'>, user: any): Promise<LostItem> => {
    if (!user) throw new Error("User must be authenticated to create a lost item");

    const { data: usedNumbers, error: fetchError } = await supabase
      .from('lost_items')
      .select('sequential_number');
    if (fetchError) throw fetchError;

    const usedSet = new Set((usedNumbers || []).map(item => item.sequential_number));
    let nextSeq = 1;
    while (usedSet.has(nextSeq) && nextSeq <= 999) {
      nextSeq++;
    }
    if (nextSeq > 999) nextSeq = 1;

    const { ...rest } = item;
    const now = new Date().toISOString();
    const username = user.user_metadata?.username || user.email || 'unknown';
    const newItem: LostItemInsert = {
      ...rest,
      sequential_number: nextSeq,
      description: item.description || "",
      history: [
        {
          status: "Created",
          date: now,
          updatedBy: username,
        }
      ],
    };
    const { data, error } = await supabase.from('lost_items').insert(newItem).select().single();
    if (error) throw error;
    return data;
};

export const updateLostItem = async ({ id, changes }: { id: number, changes: LostItemUpdate }): Promise<LostItem> => {
    const { data, error } = await supabase.from('lost_items').update(changes).eq('id', id).select().single();
    if (error) throw error;
    return data;
};

export const deleteLostItem = async (id: number) => {
    const { error } = await supabase.from('lost_items').delete().eq('id', id);
    if (error) throw error;
    return id;
}; 